import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Plus, Gem, Wallet, Backpack, Mountain, Heart, Users, Globe } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_CATEGORIES = ['luxury', 'budget', 'backpacking', 'adventure', 'honeymoon', 'family'];

const CATEGORY_ICONS = { luxury: Gem, budget: Wallet, backpacking: Backpack, adventure: Mountain, honeymoon: Heart, family: Users };
const CategoryIcon = ({ cat, className = 'h-4 w-4' }) => {
  const I = CATEGORY_ICONS[cat] || Globe;
  return <I className={className} strokeWidth={1.75} />;
};

export const TravelTab = ({ formData, handleSelectChange }) => {
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [travelCategories, setTravelCategories] = useState(() => {
    const saved = localStorage.getItem('travelCategories');
    return saved ? JSON.parse(saved) : DEFAULT_CATEGORIES;
  });

  const handleAddCategory = () => {
    if (customCategory.trim()) {
      const newCategories = [...travelCategories, customCategory.trim().toLowerCase()];
      setTravelCategories(newCategories);
      localStorage.setItem('travelCategories', JSON.stringify(newCategories));
      handleSelectChange('travelCategory', customCategory.trim().toLowerCase());
      setCustomCategory('');
      setShowCustomCategory(false);
      toast.success('New category added!');
    }
  };

  return (
    <Card className="p-6 sm:p-10 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
      <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 mb-8">Travel Preferences</h2>
      
      <div className="space-y-6">
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Travel Category *</Label>
          <Select value={formData.travelCategory} onValueChange={(v) => handleSelectChange('travelCategory', v)}>
            <SelectTrigger data-testid="select-travel-category" className="h-12 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              {travelCategories.map(cat => (
                <SelectItem key={cat} value={cat}>
                  <span className="inline-flex items-center gap-2"><CategoryIcon cat={cat} /> {cat.charAt(0).toUpperCase() + cat.slice(1)}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="p-6 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <Label className="text-sm font-semibold text-slate-700">Add Custom Category</Label>
            <Button type="button" variant="outline" size="sm" onClick={() => setShowCustomCategory(!showCustomCategory)} data-testid="toggle-custom-category" className="rounded-lg">
              <Plus className="h-4 w-4 mr-1" />New Category
            </Button>
          </div>
          
          {showCustomCategory && (
            <div className="flex gap-3 animate-fade-in">
              <Input value={customCategory} onChange={(e) => setCustomCategory(e.target.value)} data-testid="input-custom-category" placeholder="e.g., Adventure" className="h-10 rounded-lg" />
              <Button type="button" onClick={handleAddCategory} data-testid="add-custom-category-btn" className="bg-primary-700 text-white rounded-lg px-6">Add</Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
